import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Camera, Scan, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * BarcodeScannerModal - Scanner de code-barre avec html5-qrcode
 * 
 * Features:
 * - Scan de code-barre via caméra
 * - Recherche automatique sur Open Food Facts
 * - UI stylée avec feedback visuel
 * - Traductions FR/EN
 */

export const BarcodeScannerModal = ({ open, onClose, onFoodFound, authFetch, en = false }) => {
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const html5QrcodeRef = useRef(null);

  const L = (fr, eng) => (en ? eng : fr);

  useEffect(() => {
    if (open && !html5QrcodeRef.current) {
      initScanner();
    }

    return () => {
      stopScanner();
    };
  }, [open]);

  const initScanner = async () => {
    try {
      setError(null);
      const html5Qrcode = new Html5Qrcode("barcode-scanner");
      html5QrcodeRef.current = html5Qrcode;

      await html5Qrcode.start(
        { facingMode: "environment" }, // Caméra arrière
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
        },
        onScanSuccess,
        onScanFailure
      );

      setScanning(true);
    } catch (err) {
      console.error("Scanner init error:", err);
      setError(L(
        "Impossible d'accéder à la caméra. Vérifiez les autorisations.",
        "Cannot access camera. Check permissions."
      ));
    }
  };

  const stopScanner = async () => {
    if (html5QrcodeRef.current && scanning) {
      try {
        await html5QrcodeRef.current.stop();
        html5QrcodeRef.current = null;
        setScanning(false);
      } catch (err) {
        console.error("Stop scanner error:", err);
      }
    }
  };

  const onScanSuccess = async (decodedText) => {
    // Stop scanner immédiatement après scan réussi
    await stopScanner();
    setLoading(true);
    
    try {
      // Recherche sur Open Food Facts via backend
      const response = await authFetch(`/api/tracking/foods/search?q=${decodedText}&lang=${en ? 'en' : 'fr'}`);
      if (!response.ok) throw new Error('API error');
      
      const data = await response.json();
      const foods = data.foods || [];

      // Filtrer les résultats pour trouver le produit exact par code-barre
      let product = foods.find(f => f.code === decodedText);
      
      // Si pas trouvé par code exact, prendre le premier résultat (souvent correct avec OFF)
      if (!product && foods.length > 0) {
        product = foods[0];
      }

      if (product) {
        toast.success(L(`Produit trouvé : ${product.name}`, `Product found: ${product.name}`));
        onFoodFound(product);
        onClose();
      } else {
        toast.error(L(
          "Produit non trouvé dans la base de données.",
          "Product not found in database."
        ));
        setError(L(
          `Code-barre ${decodedText} non reconnu. Essayez une saisie manuelle.`,
          `Barcode ${decodedText} not recognized. Try manual entry.`
        ));
        setLoading(false);
        // Redémarrer le scanner
        await initScanner();
      }
    } catch (err) {
      console.error("Barcode search error:", err);
      toast.error(L("Erreur lors de la recherche du produit.", "Error searching for product."));
      setError(L("Erreur de connexion. Réessayez.", "Connection error. Try again."));
      setLoading(false);
      await initScanner();
    }
  };

  const onScanFailure = (errorMessage) => {
    // Silent - ne pas logger chaque échec de scan
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="barcode-scanner-modal"
        >
          <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" onClick={handleClose} />
          
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="relative w-full max-w-md bg-zinc-900 border border-white/10 rounded-3xl p-6 overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-bold text-white">
                  {L("Scanner code-barre", "Scan barcode")}
                </h3>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-lg hover:bg-white/10 text-zinc-400"
                data-testid="barcode-scanner-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Scanner Container */}
            <div className="relative bg-black rounded-2xl overflow-hidden mb-4" style={{ aspectRatio: '1' }}>
              <div id="barcode-scanner" className="w-full h-full" data-testid="barcode-scanner-camera-view" />
              
              {/* Overlay - Scan Box */}
              {scanning && !loading && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                  <motion.div
                    animate={{
                      boxShadow: [
                        '0 0 0 2px rgba(255, 23, 68, 0.5)',
                        '0 0 0 2px rgba(255, 23, 68, 1)',
                        '0 0 0 2px rgba(255, 23, 68, 0.5)',
                      ],
                    }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="w-64 h-64 border-4 border-primary rounded-2xl"
                  >
                    <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-xl" />
                    <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-xl" />
                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-xl" />
                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-xl" />
                  </motion.div>
                </div>
              )}

              {/* Loading Overlay */}
              {loading && (
                <div className="absolute inset-0 bg-black/80 flex flex-col items-center justify-center">
                  <Loader2 className="w-12 h-12 text-primary animate-spin mb-3" />
                  <p className="text-white font-medium">
                    {L("Recherche du produit...", "Searching for product...")}
                  </p>
                </div>
              )}

              {/* Error Overlay */}
              {error && !loading && (
                <div className="absolute inset-0 bg-black/90 flex flex-col items-center justify-center p-6" data-testid="barcode-scanner-error">
                  <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
                  <p className="text-white text-center text-sm mb-4">{error}</p>
                  <button
                    onClick={() => {
                      setError(null);
                      initScanner();
                    }}
                    className="px-4 py-2 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition"
                    data-testid="barcode-scanner-retry-btn"
                  >
                    {L("Réessayer", "Retry")}
                  </button>
                </div>
              )}
            </div>

            {/* Instructions */}
            {scanning && !loading && !error && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-zinc-800/50 border border-white/10">
                <Scan className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <p className="text-sm text-zinc-300 leading-relaxed">
                  {L(
                    "Placez le code-barre au centre du cadre. Le scan est automatique.",
                    "Place the barcode in the center of the frame. Scanning is automatic."
                  )}
                </p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BarcodeScannerModal;
